// ----------------------------------------------------------------------
// About / intro split section. Two-column on md+ with a staggered image
// collage, stacked single column on mobile.

import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInLeft, varFadeInRight } from '../../../components/animate';
import { SectionHeader, SectionShell } from '../common';

import {
  LINK_DOWNLOADS,
} from '../../../data/contact-info';
// ----------------------------------------------------------------------

const LIST_IMG = [
  { id: 1, url: '/assets/images/asap/about_01.jpg', alt: 'ASAP training session' },
  { id: 2, url: '/assets/images/asap/about_02.jpg', alt: 'ASAP students studying' },
];

const LIST_FEATURES = [
  {
    id: 1,
    icon: 'lucide:graduation-cap',
    title: 'Programs we cover',
    body: 'MOH, DHA, HAAD, OET and other Prometric exams.',
  },
  {
    id: 2,
    icon: 'lucide:laptop',
    title: 'Flexible class formats',
    body: 'Online and offline classes, with study material and practice tests.',
  },
  {
    id: 3,
    icon: 'lucide:users',
    title: 'Experienced trainers',
    body: 'Personalised coaching from well experienced faculty.',
  },
];


// Decorative accent rail behind the collage. Hidden on mobile where the
// images sit edge to edge.
const AccentRail = styled('span')(({ theme }) => ({
  display: { xs: 'none', md: 'block' },
  position: 'absolute',
  top: theme.spacing(4),
  insetInlineStart: { md: -theme.spacing(3), lg: -theme.spacing(5) },
  width: theme.spacing(3),
  height: '82%',
  borderStartStartRadius: 15,
  borderEndStartRadius: 15,
  backgroundColor: '#DF3E30',
  transition: 'background-color 0.4s ease',
  '@media (hover: hover)': {
    '.about-collage:hover &': { backgroundColor: '#1877F2' },
  },
}));

// ----------------------------------------------------------------------

function FeatureList({ items }) {
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        p: 0,
        listStyle: 'none',
        display: 'grid',
        gap: { xs: 1.5, md: 2 },
      }}
    >
      {items.map((item, index) => (
        <Box
          component={motion.li}
          key={item.id}
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.45, delay: index * 0.1, ease: 'easeOut' }}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.75,
            ml: -1,
            p: 1,
            borderRadius: 2,
            transition: 'background-color 0.25s ease, transform 0.25s ease',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateX(4px)',
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                '& .about-icon': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.16) },
              },
            },
          }}
        >
          <Box
            className="about-icon"
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: 'primary.main',
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
              transition: 'background-color 0.25s ease',
            }}
          >
            <Iconify icon={item.icon} width={18} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
              {item.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, lineHeight: 1.7 }}>
              {item.body}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

FeatureList.propTypes = { items: PropTypes.arrayOf(PropTypes.object).isRequired };

// ----------------------------------------------------------------------

function ImageCollage({ images, ratio = '3 / 4' }) {
  return (
    <Box
      className="about-collage"
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: { xs: 1, sm: 1.5 },
        px: { xs: 0, md: 2 },
        py: { xs: 1, md: 2 },
        borderRadius: 3,
        backgroundImage: () =>
          `radial-gradient(60% 55% at 85% 12%, ${alpha('#DF3E30', 0.12)} 0%, transparent 62%), radial-gradient(55% 50% at 5% 95%, ${alpha('#1877F2', 0.12)} 0%, transparent 60%)`,
      }}
    >
      <AccentRail />
      {images.map((image, index) => (
        <MotionInView
          key={image.id}
          variants={varFadeInLeft}
          transition={{ delay: 0.15 + index * 0.12 }}
          sx={{ mt: index % 2 === 1 ? { xs: 3, md: 6 } : 0 }}
        >
          {/* The lift lives on an inner box because framer-motion owns the
              transform of the animated wrapper. */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: '15px',
              overflow: 'hidden',
              aspectRatio: ratio,
              backgroundColor: (t) => alpha(t.palette.grey[500], 0.12),
              border: (t) => `1px solid ${t.palette.divider}`,
              boxShadow: (t) => t.customShadows.z8,
              transition: 'transform 0.4s ease, box-shadow 0.4s ease',
              '@media (hover: hover)': {
                '&:hover': { transform: 'translateY(-4px)' },
              },
            }}
          >
            <Box
              component="img"
              src={image.url}
              alt={image.alt}
              loading="lazy"
              decoding="async"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 0.6s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                '@media (hover: hover)': {
                  '.about-collage:hover &': { transform: 'scale(1.06)' },
                },
              }}
            />
          </Box>
        </MotionInView>
      ))}
    </Box>
  );
}

ImageCollage.propTypes = { images: PropTypes.arrayOf(PropTypes.object).isRequired, ratio: PropTypes.string };

// ----------------------------------------------------------------------

export default function AboutWidget() {
  return (
    <SectionShell>
      <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: 'center' }}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 2, md: 1 } }}>
          <MotionInView variants={varFadeInRight}>
            <ImageCollage images={LIST_IMG} />
          </MotionInView>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 2 } }}>
          <MotionInView variants={varFadeInLeft}>
            <SectionHeader
              align="left"
              titleVariant="h3"
              eyebrow="About us"
              title="Welcome to ASAP Marketing and Studies Consultancy"
              description="An educational institute specialising in Prometric exam preparation. We help healthcare professionals prepare for and pass their licensing exams with study material, practice tests and personalised coaching."
              sx={{ mb: { xs: 2.5, md: 3 } }}
            />
          </MotionInView>

          <FeatureList items={LIST_FEATURES} />

          <Stack
            direction="row"
            useFlexGap
            spacing={{ xs: 1.5, sm: 2 }}
            sx={{ mt: { xs: 3, md: 4 }, alignItems: 'center', flexWrap: 'wrap' }}
          >
            {LINK_DOWNLOADS.map((link) => (
              <Box
                key={link.name}
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Get the ASAP app on ${link.name}`}
                sx={{
                  display: 'block',
                  width: { xs: 140, sm: 168 },
                  borderRadius: 1.5,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '@media (hover: hover)': {
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: (t) => `0 12px 24px ${alpha(t.palette.grey[900], 0.12)}`,
                    },
                  },
                }}
              >
                <Box
                  component="img"
                  src={link.logo}
                  alt={`Download on ${link.name}`}
                  loading="lazy"
                  sx={{ width: '100%', display: 'block' }}
                />
              </Box>
            ))}

            <Button
              component={RouterLink}
              to="/about-us"
              variant="outlined"
              endIcon={<Iconify icon="eva:arrow-forward-outline" />}
              sx={{ ml: { sm: 1 } }}
            >
              More about us
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </SectionShell>
  );
}
